import React, { useState, useEffect, useCallback } from "react";
import { get, set, debounce } from "lodash";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { ColorRing } from "react-loader-spinner";
import { motion } from "framer-motion";
import { POST } from "../../api/requests";
import Fuse from "fuse.js";
import { CircleAlert } from "lucide-react";

const WorkflowForm = ({
  workflow,
  currentNode,
  contextData,
  onRunAutomation,
  interfaceDefinition,
  automationId,
  isLoading,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const initialFormData = { ...contextData };

    if (interfaceDefinition) {
      Object.entries(interfaceDefinition.interface).forEach(([field, fieldData]) => {
        if (fieldData.interface?.type === "fixedDropdown") {
          const initialValue = get(contextData, field);
          if (!initialValue) return;

          const fuseOptions = {
            keys: ['name', 'company_name'],
            threshold: 0.3,
          };

          const fuse = new Fuse(fieldData.interface.options, fuseOptions);
          const searchResult = fuse.search(initialValue);
          const selectedOption = searchResult.length > 0 ? searchResult[0].item : null;
          if (selectedOption) {
            set(initialFormData, field, selectedOption._id);
          }
        }
      });
    }

    setFormData(initialFormData);
  }, [contextData, currentNode]);

  const handleInputChange = (field, value, fieldData) => {
    if (!fieldData.interface || !fieldData.interface.lambdaPath) {
      setFormData((prevData) => {
        const newData = { ...prevData };
        set(newData, field, value);
        return newData;
      });
    }
  };

  const loadOptions = async (
    inputValue,
    callback,
    field,
    lambdaPath,
    fieldData
  ) => {
    try {
      const response = await POST("/automations/search", {
        lambdaPath,
        inputData: { query: inputValue },
        platformCredential: fieldData.interface?.platformCredential || interfaceDefinition?.platformCredential,
      });

      return response.map((item) => ({
        value: item,
        label: get(item, fieldData.interface.renderValue || 'name'),
        sublabel: fieldData.interface.sublabel ? get(item, fieldData.interface.sublabel) : undefined,
      }));
    } catch (error) {
      console.error("Error fetching search results:", error);
      return [];
    }
  };

  const debouncedLoadOptions = useCallback(
    debounce((inputValue, callback, field, lambdaPath, fieldData) => {
      loadOptions(inputValue, callback, field, lambdaPath, fieldData).then(callback);
    }, 300),
    []
  );

  const handleOptionSelect = (field, option, fieldData) => {
    setFormData((prevData) => {
      const newData = { ...prevData };
      if (option) {
        if (fieldData.interface.type === "asyncSelect") {
          set(newData, field, option.value);
        } else {
          set(newData, field, option.value);
        }
      } else {
        set(newData, field, null);
      }
      return newData;
    });
  };

  const CustomOption = ({ innerProps, label, sublabel }) => {
    return (
      <div
        {...innerProps}
        className="px-3 py-2 hover:bg-slate-100 cursor-pointer"
      >
        <div className="text-slate-900">{label}</div>
        {sublabel && <div className="text-sm text-slate-500">{sublabel}</div>}
      </div>
    );
  };

  const renderInputField = (field, fieldData) => {
    const { interface: fieldInterface } = fieldData;
    if (!fieldInterface) return null;

    const value = get(formData, field);
    const { type, lambdaPath, displayTitle, singleLine } = fieldInterface;

    let inputElement;
    if (type === "fixedDropdown") {
      const options = fieldInterface.options.map((option) => ({
        value: option._id,
        label: option[fieldInterface.renderValue || 'name'],
      }));
      
      const selectedOption = options.find(option => option.value === value);
      
      inputElement = (
        <Select
          id={field}
          name={field}
          value={selectedOption}
          onChange={(option) => handleOptionSelect(field, option, fieldData)}
          options={options}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      );
    } else if (type === "asyncSelect") {
      const selectedValue = value ? {
        value: value,
        label: get(value, fieldInterface.renderValue || 'name'),
        sublabel: fieldInterface.sublabel ? get(value, fieldInterface.sublabel) : undefined
      } : null;

      inputElement = (
        <AsyncSelect
          id={field}
          name={field}
          value={selectedValue}
          onChange={(option) => handleOptionSelect(field, option, fieldData)}
          components={{
            Option: ({ innerProps, data }) => (
              <CustomOption
                innerProps={innerProps}
                label={data.label}
                sublabel={data.sublabel}
              />
            ),
          }}
          loadOptions={(inputValue, callback) =>
            debouncedLoadOptions(
              inputValue,
              callback,
              field,
              lambdaPath,
              fieldData
            )
          }
          onInputChange={(inputValue) =>
            handleInputChange(field, inputValue, fieldData)
          }
          classNames={{
            control: () =>
              "border border-slate-300 shadow-sm rounded-md",
          }}
          placeholder="Search..."
          isClearable
          classNamePrefix="react-select"
        />
      );
    } else {
      inputElement = (
        <input
          type="text"
          id={field}
          name={field}
          value={value || ""}
          onChange={(e) =>
            handleInputChange(field, e.target.value, fieldData)
          }
          className="focus:ring-blue-500 focus:border-blue-500 block w-full text-sm border-gray-300 rounded-md"
          placeholder="Enter value"
          autoComplete="off"
        />
      );
    }

    return (
      <motion.div
        key={field}
        className={`mb-4 relative ${singleLine ? "col-span-2" : ""}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.label
          htmlFor={field}
          className="block text-xs font-medium text-slate-700 mb-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.2 }}
        >
          {displayTitle || field}
        </motion.label>
        <motion.div
          className="relative rounded-md shadow-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.2 }}
        >
          {inputElement}
        </motion.div>
        {errors[field] && (
          <div className="flex items-center space-x-1 mt-2">
            <CircleAlert color="#ff4d00" strokeWidth={2.25} size={14} />
            <p className="text-xs text-slate-800 font-medium">
              {errors[field]}
            </p>
          </div>
        )}
      </motion.div>
    );
  };

  const renderInterface = (interfaceDefinition) => {
    return (
      <motion.div
        className="relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="border-gray-200 pt-4 grid grid-cols-2 gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {Object.entries(interfaceDefinition.interface || {})
            .filter(([_, fieldData]) => fieldData.interface)
            .sort(([, a], [, b]) => (a.index || 99) - (b.index || 99))
            .map(([field, fieldData]) => renderInputField(field, fieldData))}
        </motion.div>
      </motion.div>
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.entries(interfaceDefinition.interface || {}).forEach(([field, fieldData]) => {
      if (fieldData.interface?.required) {
        const value = get(formData, field);
        if (!value) {
          newErrors[field] = "This field is required";
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setErrors({});
      onRunAutomation(automationId, formData);
    }
  };

  return (
    <div className="pt-2 pb-4 sm:px-6 lg:px-8">
      <div className="py-6 sm:px-0 pr-4">
        <h1 className="text-lg font-semibold text-gray-900 mb-1">
          {currentNode?.displayTitle}
        </h1>
        <p className="text-xs text-slate-600 mb-2">
          Review the data below to confirm accuracy before running the
          automation.
        </p>

        <div className="">
          <form onSubmit={handleSubmit}>
            {renderInterface(interfaceDefinition)}
            <div className="mt-6 justify-start flex items-center">
              <button
                type="submit"
                style={{
                  backgroundColor: "#C2FB20",
                  color: "#0F172A",
                }}
                className="py-2.5 flex hover:bg-opacity-70 items-center space-x-2 font-medium text-xs px-4 rounded-md"
              >
                {isLoading && (
                  <ColorRing
                    visible={true}
                    height="16"
                    width="16"
                    ariaLabel="blocks-loading"
                    colors={[
                      "#0F172A",
                      "#0F172A",
                      "#0F172A",
                      "#0F172A",
                      "#0F172A",
                    ]}
                  />
                )}
                <p>
                  {currentNode?.confirmationDialogButtonText
                    ? currentNode?.confirmationDialogButtonText
                    : (workflow?.status === "completed" ? "Rerun" : "Run") +
                      " automation"}
                </p>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkflowForm;
